import React, { useState, useEffect } from 'react';
import {
    Box, Container, Typography, Avatar, Chip, CircularProgress,
    Stack, Paper, Button, IconButton, TextField, MenuItem,
    Dialog, DialogTitle, DialogContent, DialogActions, Alert,
    InputAdornment, Autocomplete
} from '@mui/material';
import ArrowBackIcon    from '@mui/icons-material/ArrowBack';
import EditIcon         from '@mui/icons-material/Edit';
import StarIcon         from '@mui/icons-material/Star';
import StarBorderIcon   from '@mui/icons-material/StarBorder';
import Visibility       from '@mui/icons-material/Visibility';
import VisibilityOff    from '@mui/icons-material/VisibilityOff';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PauseCircleIcon  from '@mui/icons-material/PauseCircle';
import PlayCircleIcon   from '@mui/icons-material/PlayCircle';
import TranslateIcon    from '@mui/icons-material/Translate';
import CloseIcon        from '@mui/icons-material/Close';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation }         from 'react-i18next';
import axios                      from '../api/axiosConfig';
import axiosPublic                from '../api/axiosPublic';
import { useAuth }                from '../context/AuthContext';

const TERRACOTTA = '#C4622D';
const WALNUT     = '#2C1F14';
const FOREST     = '#4A6741';
const GOLD       = '#C9973A';
const PARCHMENT  = '#F7F3EE';

function InfoRow({ icon, label, value }) {
    return (
        <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{
            py: 1.5, borderBottom: '1px solid rgba(196,98,45,0.08)',
            '&:last-child': { border: 'none' },
        }}>
            <Box sx={{ color: TERRACOTTA, mt: 0.2, flexShrink: 0 }}>{icon}</Box>
            <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600,
                    textTransform: 'uppercase', letterSpacing: '0.07em', fontSize: '0.68rem' }}>
                    {label}
                </Typography>
                <Typography variant="body2" fontWeight={600} sx={{ color: value ? WALNUT : 'text.disabled' }}>
                    {value || '—'}
                </Typography>
            </Box>
        </Stack>
    );
}

export default function GuideProfilePage() {
    const { id }   = useParams();
    const { user } = useAuth();
    const navigate = useNavigate();
    const { t }    = useTranslation();

    const isAdmin   = user?.role === 'ADMIN';
    const isManager = user?.role === 'MANAGER';
    const isGuide   = user?.role === 'GUIDE';
    const canAccess = isAdmin || isManager;

    const [guide,      setGuide]      = useState(null);
    const [districts,  setDistricts]  = useState([]);
    const [loading,    setLoading]    = useState(true);
    const [notAllowed, setNotAllowed] = useState(false);
    const [editOpen,   setEditOpen]   = useState(false);

    // Edit form — empty strings mean "no change"
    const [form,       setForm]       = useState({ districtId: '', startDate: '', stars: '' });
    const [saving,     setSaving]     = useState(false);
    const [formError,  setFormError]  = useState('');

    // Password confirmation
    const [pwdOpen,    setPwdOpen]    = useState(false);
    const [password,   setPassword]   = useState('');
    const [showPwd,    setShowPwd]    = useState(false);
    const [pwdError,   setPwdError]   = useState('');
    const [pwdLoading, setPwdLoading] = useState(false);

    // Ratings
    const [ratingSummary, setRatingSummary] = useState(null);
    const [ratings,       setRatings]       = useState([]);

    // Status toggle (inactivate/reactivate)
    const [statusLoading, setStatusLoading] = useState(false);
    const [statusError,   setStatusError]   = useState('');

    // Language management
    const [allLanguages,  setAllLanguages]  = useState([]);
    const [selectedLang,  setSelectedLang]  = useState(null);
    const [langLoading,   setLangLoading]   = useState(false);
    const [langError,     setLangError]     = useState('');

    // No redirect — all authenticated users can view the guide profile

    useEffect(() => {
        if (!id) return;
        Promise.all([
            axios.get(`/guides/view/id/${id}`),
            canAccess ? axios.get('/districts/view?size=1000') : Promise.resolve({ data: [] }),
            axiosPublic.get(`/api/ratings/summary?targetId=${id}&targetType=GUIDE`).catch(() => ({ data: null })),
            axiosPublic.get(`/api/ratings/target?targetId=${id}&targetType=GUIDE`).catch(() => ({ data: [] })),
            axios.get('/languages/view?size=1000').catch(() => ({ data: { content: [] } })),
        ])
            .then(([gRes, dRes, summaryRes, ratingsRes, langRes]) => {
                setGuide(gRes.data);
                setDistricts(Array.isArray(dRes.data) ? dRes.data : (dRes.data?.content || []));
                setRatingSummary(summaryRes.data);
                setRatings(Array.isArray(ratingsRes.data) ? ratingsRes.data : []);
                const langs = langRes.data?.content || langRes.data || [];
                setAllLanguages(Array.isArray(langs) ? langs : []);
            })
            .catch(err => {
                if (err.response?.status === 403) setNotAllowed(true);
            })
            .finally(() => setLoading(false));
    }, [id]);

    /* ── FIX: Pre-fill the form from current guide data when opening the edit dialog,
       NOT on initial load. This ensures the "changed" detection compares against
       the actual current values at the moment the user clicks Edit. ─────────── */
    const openEdit = () => {
        setForm({
            districtId: guide?.districtId || '',
            startDate:  guide?.startDate  || '',
            stars:      guide?.stars       ?? 0,
        });
        setFormError('');
        setEditOpen(true);
    };
    const closeEdit = () => setEditOpen(false);

    /* ── FIX: Detect changes by comparing form values to current guide state.
       Previously, the validation only checked truthiness of form fields, meaning
       unchanged pre-filled values always passed and "nothing changed" never
       triggered correctly. Now we compare each field explicitly. ────────────── */
    const submitEdit = (e) => {
        e.preventDefault();
        const districtChanged = form.districtId && form.districtId !== (guide?.districtId || '');
        const dateChanged     = form.startDate  && form.startDate  !== (guide?.startDate  || '');
        const starsChanged    = isAdmin && Number(form.stars) !== (guide?.stars ?? 0);

        if (!districtChanged && !dateChanged && !starsChanged) {
            setFormError(t('guide_profile.nothing_changed', 'No changes to save.'));
            return;
        }
        setFormError('');
        setEditOpen(false);
        setPwdOpen(true);
    };

    const toggleGuideStatus = async () => {
        const newStatus = guide.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
        setStatusLoading(true);
        setStatusError('');
        try {
            const res = await axios.patch(`/guides/${id}/status?status=${newStatus}`);
            setGuide(prev => ({ ...prev, status: res.data.status }));
        } catch {
            setStatusError(t('guide_profile.status_failed', 'Failed to update status.'));
        } finally {
            setStatusLoading(false);
        }
    };

    const addLanguage = async () => {
        if (!selectedLang) return;
        setLangLoading(true); setLangError('');
        try {
            const res = await axios.post(`/guides/${id}/languages?languageId=${selectedLang.id}`);
            setGuide(prev => ({ ...prev, languageIds: res.data.languageIds, languageNames: res.data.languageNames }));
            setSelectedLang(null);
        } catch (err) {
            setLangError(err?.response?.data?.message || 'Failed to add language.');
        } finally {
            setLangLoading(false);
        }
    };

    const removeLanguage = async (langId) => {
        setLangLoading(true); setLangError('');
        try {
            const res = await axios.delete(`/guides/${id}/languages/${langId}`);
            setGuide(prev => ({ ...prev, languageIds: res.data.languageIds, languageNames: res.data.languageNames }));
        } catch (err) {
            setLangError(err?.response?.data?.message || 'Failed to remove language.');
        } finally {
            setLangLoading(false);
        }
    };

    const closePwd = () => { setPwdOpen(false); setPassword(''); setPwdError(''); setShowPwd(false); };

    const confirmSave = async () => {
        if (!password) { setPwdError(t('users.password_required')); return; }
        setPwdLoading(true);
        setPwdError('');
        try {
            await axios.post('/api/app_user/login', { email: user.email, password });

            /* ── FIX: Always send all editable fields, not just "changed" ones.
               The backend GuideAssignmentDto null-checks each field, so sending
               the current (unchanged) value is harmless. This prevents the bug
               where only districtId was sent because it was the only truthy field,
               while startDate and stars were silently dropped. ─────────────────── */
            const payload = {};
            if (form.districtId) payload.districtId = form.districtId;
            if (form.startDate)  payload.startDate  = form.startDate;
            if (isAdmin)         payload.stars       = Number(form.stars);

            const res = await axios.patch(`/guides/${id}/assignment`, payload);
            setGuide(prev => ({ ...prev, ...res.data }));
            closePwd();
        } catch (err) {
            const code = err.response?.data?.error;
            const st   = err.response?.status;
            setPwdError(
                st === 401 || code === 'WRONG_PASSWORD' || code === 'INVALID_CREDENTIALS'
                    ? t('users.wrong_password')
                    : t('guide_profile.save_failed', 'Failed to save.')
            );
        } finally {
            setPwdLoading(false);
        }
    };

    const isOwnGuideProfile = isGuide && guide && user?.id === guide?.id;
    const canManageLanguages = isAdmin || isManager || isOwnGuideProfile;

    if (!user) return null;

    if (loading) return (
        <Box sx={{ minHeight: '100vh', bgcolor: PARCHMENT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress sx={{ color: TERRACOTTA }} />
        </Box>
    );

    if (notAllowed) return (
        <Box sx={{ minHeight: '100vh', bgcolor: PARCHMENT, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 2 }}>
            <Typography sx={{ color: WALNUT }}>{t('guide_profile.not_allowed', 'Access denied.')}</Typography>
            <Button onClick={() => navigate(-1)} sx={{ color: TERRACOTTA }}>
                {t('common.back', 'Back')}
            </Button>
        </Box>
    );

    if (!guide) return null;

    /* ── FIX: Resolve district label using name first, then region as fallback.
       The `districtName` from the DTO may be null if the District entity's `name`
       column is empty. Fall back to the districts list, trying name then region. */
    const districtLabel = guide.districtName
        || districts.find(d => d.id === guide.districtId)?.name
        || districts.find(d => d.id === guide.districtId)?.region
        || '—';

    const assignedLangIds = new Set(guide?.languageIds || []);
    const availableLanguages = allLanguages.filter(l => !assignedLangIds.has(l.id));

    return (
        <Box sx={{ minHeight: '100vh', background: `linear-gradient(rgba(247,243,238,0.84), rgba(247,243,238,0.84)), url("https://upload.wikimedia.org/wikipedia/commons/d/d5/Yerevan_Armenia_with_the_backdrop_of_Mount_Ararat.JPG") center/cover fixed` }}>
            {/* Banner */}
            <Box sx={{
                background: `linear-gradient(160deg, ${WALNUT} 0%, #6B3A20 50%, ${FOREST} 100%)`,
                height: 130, position: 'relative', overflow: 'hidden',
                '&::after': { content: '""', position: 'absolute', bottom: 0, left: 0, right: 0,
                    height: '40px', background: PARCHMENT, borderRadius: '40px 40px 0 0' },
            }} />

            <Container maxWidth="md" sx={{ mt: -4, pb: 8, position: 'relative', zIndex: 1 }}>

                {/* Back row */}
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                    <IconButton onClick={() => navigate(-1)} size="small"
                                sx={{ color: TERRACOTTA, border: '1px solid rgba(196,98,45,0.2)', borderRadius: 2 }}>
                        <ArrowBackIcon fontSize="small" />
                    </IconButton>
                    <Typography variant="overline" sx={{ color: FOREST, letterSpacing: '0.2em', fontWeight: 700 }}>
                        {t('guide_profile.overline', 'Guide Profile')}
                    </Typography>
                </Stack>

                {/* Profile header */}
                <Paper elevation={0} sx={{
                    p: 3, borderRadius: 3, mb: 3, display: 'flex', alignItems: 'center', gap: 3,
                    border: '1px solid rgba(196,98,45,0.1)', bgcolor: 'white',
                    boxShadow: '0 2px 16px rgba(44,31,20,0.07)',
                }}>
                    <Avatar src={guide.imageUrl} alt={guide.username}
                            sx={{ width: 72, height: 72, bgcolor: FOREST,
                                border: '3px solid rgba(74,103,65,0.2)', flexShrink: 0 }}>
                        {guide.username?.[0]?.toUpperCase()}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="h5" fontWeight={800}
                                    sx={{ color: WALNUT, fontFamily: "'Playfair Display', Georgia, serif" }}>
                            {guide.username}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>{guide.email}</Typography>
                        <Stack direction="row" spacing={1} mt={0.5} flexWrap="wrap" gap={0.5}>
                            <Chip label="GUIDE" size="small"
                                  sx={{ bgcolor: FOREST, color: 'white', fontWeight: 700, fontSize: '0.7rem' }} />
                            {guide.status === 'INACTIVE' && (
                                <Chip label={t('guide_profile.inactive', 'INACTIVE')} size="small"
                                      sx={{ bgcolor: '#8B2020', color: 'white', fontWeight: 700, fontSize: '0.7rem' }} />
                            )}
                            {guide.stars != null && (
                                <Chip icon={<StarIcon sx={{ fontSize: '0.85rem !important', color: `${GOLD} !important` }} />}
                                      label={`${guide.stars} ${t('districts.stars', 'stars')}`}
                                      size="small"
                                      sx={{ bgcolor: 'rgba(201,151,58,0.1)', color: WALNUT, fontWeight: 700, fontSize: '0.7rem' }} />
                            )}
                        </Stack>
                    </Box>
                    {canAccess && (
                        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                            {guide.status === 'INACTIVE' ? (
                                <Button variant="outlined" startIcon={<PlayCircleIcon />}
                                        onClick={toggleGuideStatus} disabled={statusLoading}
                                        sx={{ borderColor: FOREST, color: FOREST, borderRadius: 2, fontWeight: 700,
                                            '&:hover': { bgcolor: 'rgba(74,103,65,0.06)' } }}>
                                    {t('guide_profile.reactivate', 'Reactivate')}
                                </Button>
                            ) : (
                                <Button variant="outlined" startIcon={<PauseCircleIcon />}
                                        onClick={toggleGuideStatus} disabled={statusLoading}
                                        sx={{ borderColor: '#8B2020', color: '#8B2020', borderRadius: 2, fontWeight: 700,
                                            '&:hover': { bgcolor: 'rgba(139,32,32,0.06)' } }}>
                                    {t('guide_profile.inactivate', 'Inactivate')}
                                </Button>
                            )}
                            <Button variant="outlined" startIcon={<EditIcon />} onClick={openEdit}
                                    sx={{ borderColor: TERRACOTTA, color: TERRACOTTA, borderRadius: 2, fontWeight: 700,
                                        '&:hover': { bgcolor: 'rgba(196,98,45,0.06)' } }}>
                                {t('common.edit')}
                            </Button>
                        </Stack>
                    )}
                </Paper>
                {statusError && <Alert severity="error" sx={{ mb: 2 }}>{statusError}</Alert>}

                {/* Guide info */}
                <Paper elevation={0} sx={{
                    borderRadius: 3, border: '1px solid rgba(196,98,45,0.1)',
                    bgcolor: 'white', boxShadow: '0 2px 16px rgba(44,31,20,0.07)',
                }}>
                    <Box sx={{ px: 3, py: 2.5, borderBottom: '1px solid rgba(196,98,45,0.1)' }}>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ color: WALNUT }}>
                            {t('guide_profile.assignment', 'Assignment')}
                        </Typography>
                    </Box>
                    <Box sx={{ px: 3, py: 1 }}>
                        <InfoRow icon={<LocationCityIcon fontSize="small" />}
                                 label={t('guide_profile.district', 'District')} value={districtLabel} />
                        <InfoRow icon={<CalendarMonthIcon fontSize="small" />}
                                 label={t('guide_profile.start_date', 'Start Date')}
                                 value={guide.startDate
                                     ? new Date(guide.startDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                                     : null} />
                        {isAdmin && (
                            <InfoRow icon={<StarIcon fontSize="small" />}
                                     label={t('guide_profile.stars', 'Stars')} value={guide.stars?.toString()} />
                        )}
                    </Box>
                </Paper>

                {/* ── Languages ──────────────────────────────────────────────── */}
                {canManageLanguages && (
                    <Paper elevation={0} sx={{
                        borderRadius: 3, border: '1px solid rgba(196,98,45,0.1)',
                        bgcolor: 'white', boxShadow: '0 2px 16px rgba(44,31,20,0.07)', mt: 3,
                    }}>
                        <Box sx={{ px: 3, py: 2.5, borderBottom: '1px solid rgba(196,98,45,0.1)',
                            display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <TranslateIcon sx={{ color: TERRACOTTA, fontSize: '1.1rem' }} />
                            <Typography variant="subtitle1" fontWeight={700} sx={{ color: WALNUT }}>
                                {t('guide_profile.languages', 'Languages Spoken')}
                            </Typography>
                        </Box>
                        <Box sx={{ px: 3, py: 2.5 }}>
                            {/* Current languages */}
                            <Stack direction="row" flexWrap="wrap" gap={1} mb={2.5} minHeight={32}>
                                {(guide.languageNames || []).length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">
                                        {t('guide_profile.no_languages', 'No languages added yet.')}
                                    </Typography>
                                ) : (guide.languageNames || []).map((name, i) => (
                                    <Chip
                                        key={guide.languageIds[i]}
                                        label={name}
                                        size="small"
                                        icon={<TranslateIcon sx={{ fontSize: '0.85rem !important', color: `${FOREST} !important` }} />}
                                        onDelete={canManageLanguages ? () => removeLanguage(guide.languageIds[i]) : undefined}
                                        deleteIcon={<CloseIcon sx={{ fontSize: '0.8rem !important' }} />}
                                        sx={{ bgcolor: 'rgba(74,103,65,0.1)', color: WALNUT, fontWeight: 600,
                                            border: '1px solid rgba(74,103,65,0.2)' }}
                                    />
                                ))}
                            </Stack>

                            {/* Add language */}
                            <Stack direction="row" spacing={1.5} alignItems="center">
                                <Autocomplete
                                    options={availableLanguages}
                                    getOptionLabel={opt => opt.language || opt.code || ''}
                                    value={selectedLang}
                                    onChange={(_, v) => setSelectedLang(v)}
                                    size="small"
                                    sx={{ flexGrow: 1 }}
                                    renderInput={params => (
                                        <TextField {...params} label={t('guide_profile.add_language', 'Add a language')}
                                                   placeholder={t('guide_profile.select_language', 'Select…')} />
                                    )}
                                />
                                <Button
                                    variant="contained"
                                    disabled={!selectedLang || langLoading}
                                    onClick={addLanguage}
                                    sx={{ flexShrink: 0, bgcolor: TERRACOTTA, borderRadius: 2, fontWeight: 700,
                                        '&:hover': { bgcolor: '#9A4A1F' }, '&.Mui-disabled': { opacity: 0.45 } }}
                                >
                                    {langLoading ? <CircularProgress size={18} sx={{ color: 'white' }} /> : t('common.add', 'Add')}
                                </Button>
                            </Stack>
                            {langError && <Alert severity="error" sx={{ mt: 1.5, borderRadius: 2 }}>{langError}</Alert>}
                        </Box>
                    </Paper>
                )}

                {/* ── Ratings ─────────────────────────────────────────────────── */}
                <Paper elevation={0} sx={{
                    borderRadius: 3, border: '1px solid rgba(196,98,45,0.1)',
                    bgcolor: 'white', boxShadow: '0 2px 16px rgba(44,31,20,0.07)', mt: 3,
                }}>
                    <Box sx={{ px: 3, py: 2.5, borderBottom: '1px solid rgba(196,98,45,0.1)',
                        display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ color: WALNUT }}>
                            {t('guide_profile.ratings', 'Customer Ratings')}
                        </Typography>
                        {ratingSummary?.ratingCount > 0 && (
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                {[1,2,3,4,5].map(s => (
                                    s <= Math.round(ratingSummary.averageScore)
                                        ? <StarIcon key={s} sx={{ fontSize: '1rem', color: GOLD }} />
                                        : <StarBorderIcon key={s} sx={{ fontSize: '1rem', color: GOLD }} />
                                ))}
                                <Typography variant="body2" fontWeight={700} sx={{ color: WALNUT, ml: 0.5 }}>
                                    {ratingSummary.averageScore?.toFixed(1)} ({ratingSummary.ratingCount})
                                </Typography>
                            </Stack>
                        )}
                    </Box>
                    <Box sx={{ px: 3, py: 2 }}>
                        {ratings.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                                {t('guide_profile.no_ratings', 'No ratings yet.')}
                            </Typography>
                        ) : (
                            <Stack spacing={2}>
                                {ratings.map(r => (
                                    <Box key={r.id} sx={{ pb: 2, borderBottom: '1px solid rgba(196,98,45,0.07)',
                                        '&:last-child': { border: 'none', pb: 0 } }}>
                                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                                            <Avatar src={r.reviewerImageUrl}
                                                    sx={{ width: 28, height: 28, bgcolor: TERRACOTTA, fontSize: '0.75rem' }}>
                                                {r.reviewerName?.[0]?.toUpperCase()}
                                            </Avatar>
                                            <Typography variant="body2" fontWeight={600} sx={{ color: WALNUT }}>
                                                {r.reviewerName || t('guide_profile.anonymous', 'Anonymous')}
                                            </Typography>
                                            <Stack direction="row" spacing={0.2}>
                                                {[1,2,3,4,5].map(s => (
                                                    s <= r.score
                                                        ? <StarIcon key={s} sx={{ fontSize: '0.85rem', color: GOLD }} />
                                                        : <StarBorderIcon key={s} sx={{ fontSize: '0.85rem', color: GOLD }} />
                                                ))}
                                            </Stack>
                                            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                                                {r.createdAt ? new Date(r.createdAt).toLocaleDateString('en-GB') : ''}
                                            </Typography>
                                        </Stack>
                                        {r.comment && (
                                            <Typography variant="body2" color="text.secondary" sx={{ ml: 4.5 }}>
                                                {r.comment}
                                            </Typography>
                                        )}
                                    </Box>
                                ))}
                            </Stack>
                        )}
                    </Box>
                </Paper>
            </Container>

            {/* ── Edit dialog ─────────────────────────────────────────────────── */}
            <Dialog open={editOpen} onClose={closeEdit} fullWidth maxWidth="xs"
                    PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle fontWeight={700} sx={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                    {t('guide_profile.edit_title', 'Update Assignment')}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }} component="form" onSubmit={submitEdit}>
                        {/* ── FIX: Display district name (with region fallback) in the picklist ── */}
                        <TextField
                            label={t('guide_profile.district', 'District')}
                            value={form.districtId}
                            onChange={e => setForm(f => ({ ...f, districtId: e.target.value }))}
                            select fullWidth>
                            <MenuItem value=""><em>{t('guide_profile.no_change', '— No change —')}</em></MenuItem>
                            {districts.map(d => (
                                <MenuItem key={d.id} value={d.id}>
                                    {d.name || d.region || d.id}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            label={t('guide_profile.start_date', 'Start Date')}
                            type="date"
                            value={form.startDate}
                            onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                        />
                        {isAdmin && (
                            <TextField
                                label={t('guide_profile.stars', 'Stars')}
                                type="number"
                                value={form.stars}
                                onChange={e => setForm(f => ({ ...f, stars: e.target.value }))}
                                fullWidth inputProps={{ min: 0, max: 5 }}
                            />
                        )}
                        {formError && <Alert severity="warning">{formError}</Alert>}
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    <Button onClick={closeEdit}>{t('common.cancel')}</Button>
                    <Button variant="contained" onClick={submitEdit}
                            sx={{ bgcolor: TERRACOTTA, '&:hover': { bgcolor: '#9A4A1F' }, borderRadius: 2, fontWeight: 700 }}>
                        {t('common.next', 'Next')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Password confirmation ───────────────────────────────────────── */}
            <Dialog open={pwdOpen} onClose={closePwd} fullWidth maxWidth="xs"
                    PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle fontWeight={700} sx={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                    {t('users.confirm_title')}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {t('guide_profile.confirm_text', 'Enter your password to confirm the changes.')}
                    </Typography>
                    <TextField
                        fullWidth
                        label={t('users.your_password')}
                        type={showPwd ? 'text' : 'password'}
                        value={password}
                        onChange={e => { setPassword(e.target.value); setPwdError(''); }}
                        onKeyDown={e => { if (e.key === 'Enter') confirmSave(); }}
                        autoFocus
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setShowPwd(p => !p)} edge="end">
                                        {showPwd ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    {pwdError && <Alert severity="error" sx={{ mt: 1.5 }}>{pwdError}</Alert>}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    <Button onClick={closePwd} disabled={pwdLoading}>{t('common.cancel')}</Button>
                    <Button variant="contained" onClick={confirmSave} disabled={pwdLoading}
                            sx={{ bgcolor: TERRACOTTA, '&:hover': { bgcolor: '#9A4A1F' }, borderRadius: 2, fontWeight: 700 }}>
                        {pwdLoading ? t('common.saving') : t('users.confirm_btn')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}